import { useMemo, useState } from 'react';

import { RouterLinkAs } from '@/lib/router-link';
import {
  AlertDialog, Body, Button, Card, Eyebrow,
  I,
  Label, List, PageHeader, RailRow, Row, SearchInput, Stack, useDelayedLoading,
} from '@leanin/ui';
import { GroupRow } from '../../cards/index';
import type { GroupSummary } from '../../../lib/views/groups';
import { useCircles, useNetworkCircles } from '../../../lib/queries/groups';
import {
  useLinkCircleToNetwork,
  useUnlinkCircleFromNetwork,
} from '../../../lib/mutations/groups';
import { useConfirmDialog } from '../../../lib/use-confirm-dialog';
import { NetworkCirclesSectionSkeleton } from './NetworkCirclesSection.skeleton';

type Props = {
  readonly networkId: string;
};

const TONE_CYCLE = ['burgundy', 'navy', 'emerald', 'yellow', 'orange'] as const satisfies ReadonlyArray<'burgundy' | 'navy' | 'emerald' | 'yellow' | 'orange'>;

function circleTone(idx: number): 'burgundy' | 'navy' | 'emerald' | 'yellow' | 'orange' {
  return TONE_CYCLE[idx % TONE_CYCLE.length]!;
}

const SEARCH_PAGE_SIZE = 8;

export function NetworkCirclesSection({ networkId }: Props) {
  // Network admin view surfaces archived circles too — leaders need to see
  // them to manage / unlink. Public-facing surfaces keep the default
  // (`includeArchived: false`).
  const circlesQuery = useNetworkCircles(networkId, {
    pageSize: 50,
    includeArchived: true,
  });

  const linkMutation = useLinkCircleToNetwork();
  const unlinkMutation = useUnlinkCircleFromNetwork();
  const [searchQuery, setSearchQuery] = useState('');
  const [linkError, setLinkError] = useState<string | null>(null);
  const unlink = useConfirmDialog<{ circleId: string; circleName: string }>();
  const isPending = linkMutation.isPending || unlinkMutation.isPending;

  const trimmedQuery = searchQuery.trim();
  const candidatesQuery = useCircles(
    { q: trimmedQuery, pageSize: SEARCH_PAGE_SIZE },
    { enabled: trimmedQuery.length >= 2 },
  );

  const circles: ReadonlyArray<GroupSummary> = useMemo(
    () => circlesQuery.data?.items ?? [],
    [circlesQuery.data],
  );
  const linkedIds = useMemo(() => new Set(circles.map((c) => c.id)), [circles]);
  const candidates = useMemo(() => {
    const items = candidatesQuery.data?.items ?? [];
    // Don't surface circles that are already linked to this network. Circles
    // attached to a DIFFERENT network still appear but flagged — the server
    // refuses the link and we surface that as inline error.
    return items.filter((c) => !linkedIds.has(c.id));
  }, [candidatesQuery.data, linkedIds]);

  const handleSelect = (circle: GroupSummary) => {
    setLinkError(null);
    linkMutation.mutate(
      { networkId, circleId: circle.id },
      {
        onSuccess: () => setSearchQuery(''),
        onError: (err) =>
          setLinkError(
            err instanceof Error
              ? `Failed to link ${circle.name}: ${err.message}`
              : `Failed to link ${circle.name}.`,
          ),
      },
    );
  };

  const handleUnlink = (circleId: string, circleName: string) => {
    unlink.request({ circleId, circleName });
  };

  const confirmUnlink = () => {
    if (unlink.target === null) return;
    unlinkMutation.mutate({ networkId, circleId: unlink.target.circleId });
  };

  const showSkeleton = useDelayedLoading(circlesQuery.data === undefined);
  if (showSkeleton) {
    return <NetworkCirclesSectionSkeleton rows={3} />;
  }
  if (circlesQuery.data === undefined) return null;

  return (
    <Stack gap="lg">
      <PageHeader
        title="Circles"
        subtitle="Add or remove Circles linked to this Network."
      />

      {/* Add an existing Circle (search) OR start a new one (CTA). Both
          paths produce the same end state — a circle linked to this
          network — so they share one card. The CTA navigates to the
          standard Start-a-Circle form with `parentNetworkId` set; the
          server's create-circle handler enforces leadership and persists
          the link in the same transaction as the create. */}
      <Card pad="default">
        <Stack gap="md">
          <Eyebrow tone="accent">Add a Circle</Eyebrow>
          <Row gap="sm" align="center">
            <div style={{ flex: 1, minWidth: 0 }}>
              <SearchInput
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search Circles by name…"
                aria-label="Search Circles to add"
              />
            </div>
            <Button
              variant="primary"
              size="md"
              icon={I.plus}
              href={`/circles/new?parentNetworkId=${networkId}`}
              linkAs={RouterLinkAs}
            >
              Start a Circle
            </Button>
          </Row>
          {trimmedQuery.length < 2 ? null : candidatesQuery.isLoading ? (
            <Body size="sm" color="mute">Searching…</Body>
          ) : candidates.length === 0 ? (
            <Body size="sm" color="mute">
              No Circles match &ldquo;{trimmedQuery}&rdquo;.
            </Body>
          ) : (
            <Stack gap="none" style={{ maxHeight: 320, overflow: 'auto' }}>
              {candidates.map((circle) => {
                const leader = circle.memberPreview[0];
                const leaderName = leader
                  ? `${leader.firstName} ${leader.lastName}`.trim()
                  : null;
                const memberLabel = `${circle.memberCount} ${circle.memberCount === 1 ? 'member' : 'members'}`;
                const alreadyLinked = circle.parentGroupId !== null;
                const baseSubtitle = leaderName !== null
                  ? `Led by ${leaderName} · ${memberLabel}`
                  : memberLabel;
                const subtitle = alreadyLinked
                  ? `${baseSubtitle} · Currently in another Network`
                  : baseSubtitle;
                return (
                  <RailRow
                    key={circle.id}
                    onClick={isPending ? undefined : () => handleSelect(circle)}
                    bordered={false}
                    padding="10px 0"
                  >
                    <Stack gap="xs" style={{ flex: 1, minWidth: 0 }}>
                      <Label>{circle.name}</Label>
                      <Body size="sm" color="mute">{subtitle}</Body>
                    </Stack>
                    <Body size="sm" color="primary" style={{ whiteSpace: 'nowrap' }}>
                      {alreadyLinked ? 'Move here' : '+ Add'}
                    </Body>
                  </RailRow>
                );
              })}
            </Stack>
          )}
          {linkError !== null ? (
            <Body size="sm" color="destructive">{linkError}</Body>
          ) : null}
        </Stack>
      </Card>

      <Card pad="flush">
        {circles.length === 0 ? (
          <Row p="xl" justify="center">
            <Body size="sm" color="mute">No Circles linked to this Network yet.</Body>
          </Row>
        ) : (
          <List bordered={false}>
            {circles.map((circle, idx) => {
              const tone = circleTone(idx);
              const leader = circle.memberPreview[0];
              const leaders = leader !== undefined
                ? [{
                    name: `${leader.firstName} ${leader.lastName}`,
                    ...(leader.photoUrl !== null ? { photo: leader.photoUrl } : {}),
                  }]
                : [];
              return (
                <GroupRow
                  key={circle.id}
                  kind="circle"
                  href={`/circles/${circle.id}`}
                  ariaLabel={`Open ${circle.name}`}
                  linkAs={RouterLinkAs}
                  name={circle.name}
                  tone={tone}
                  memberCount={circle.memberCount}
                  closed={circle.access !== 'public'}
                  archived={circle.status === 'archived'}
                  leaders={leaders}
                  trailing={
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleUnlink(circle.id, circle.name)}
                      disabled={isPending}
                    >
                      Remove
                    </Button>
                  }
                />
              );
            })}
          </List>
        )}
      </Card>

      <AlertDialog
        open={unlink.open}
        onOpenChange={unlink.onOpenChange}
        title={unlink.target !== null ? `Remove "${unlink.target.circleName}" from this Network?` : 'Remove Circle?'}
        description="The Circle itself is not deleted — it just won't appear under this Network anymore."
        actionLabel="Remove Circle"
        cancelLabel="Keep Circle"
        destructive
        onAction={confirmUnlink}
      />
    </Stack>
  );
}
